import React, { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { get, toNumber } from 'lodash';

import ProductImage from 'components/consumer/cart/components/ProductImage';
import ProtectionPlans from 'components/consumer/ProtectionPlans';
import { UPDATE_CART_ITEM_PROTECTION_PLAN } from 'mocks/mutations';
import CheckoutSelectedProtectionPlan from './CheckoutSelectedProtectionPlan';

const CheckoutProtectionPlanItem = ({
  client,
  cartItem,
  getCartIssues,
  calculateTotal,
}) => {
  const [isRemovePPLoading, setIsRemovePPLoading] = useState(false);
  const [isRemovePPError, setIsRemovePPError] = useState(null);

  const refreshCheckout = useCallback(() => {
    if (getCartIssues) getCartIssues();
    if (calculateTotal) calculateTotal();
  }, [calculateTotal, getCartIssues]);

  const removeProtectionPlanOnCartItem = useCallback(async () => {
    const cartItemId = cartItem.id;
    try {
      let success = null;
      try {
        const response = await client.mutate({
          mutation: UPDATE_CART_ITEM_PROTECTION_PLAN,
          variables: {
            cart_id: toNumber(cartItemId),
            plan_id: null,
            protection_plan: false,
          },
        });

        success = get(response, 'data.updateCartItemProtectionPlan', null);
      } catch (e) {
        setIsRemovePPError('Error occurred. Please try again.');
      }
      if (success) {
        refreshCheckout();
      }
      setIsRemovePPLoading(false);
    } catch (e) {
      setIsRemovePPLoading(false);
    }
  }, [cartItem.id, client, refreshCheckout]);

  useEffect(() => {
    if (isRemovePPLoading) {
      setIsRemovePPError(null);
      removeProtectionPlanOnCartItem();
    }
  }, [isRemovePPLoading, removeProtectionPlanOnCartItem]);

  const hasNoPlans = cartItem.eligible_protection_plans?.length === 0;

  return (
    <div
      key={cartItem.id}
      className={cx(
        'relative mb-10 border border-solid [border-color:#dbdbdb]',
        {
          'bg-gray-light8': !!isRemovePPError,
        }
      )}
    >
      <div
        className={cx(
          'flex items-center p-[7.5px] bg-gray-light8 lg:[border-bottom:1px_solid_#dbdbdb] max-[1023px]:flex-wrap',
          {
            '[border-bottom:none]': hasNoPlans || !!isRemovePPError,
          }
        )}
      >
        <ProductImage product={cartItem} height={80} />
        <div>
          <div className="text-base font-bold lg:max-w-[180px] lg:max-[1200px]:text-[0.7rem] lg:max-[1200px]:leading-[unset] lg:max-[1200px]:pr-[5px]">
            {cartItem.name}
          </div>
          <div className="text-xs text-gray">Qty: {cartItem.quantity}</div>
          {hasNoPlans && (
            <div className="text-xs text-gray max-[900px]:whitespace-normal max-[900px]:max-w-[180px]   ">
              Sorry, there are no protection plans available for this item!
            </div>
          )}
        </div>

        {cartItem.selected_protection_plan && (
          <CheckoutSelectedProtectionPlan
            planName={cartItem.selected_protection_plan.plan_name}
            planPrice={cartItem.selected_protection_plan.plan_price}
            isRemovePPLoading={isRemovePPLoading}
            setIsRemovePPLoading={setIsRemovePPLoading}
          />
        )}
      </div>
      {isRemovePPError && (
        <div className="text-sm font-bold text-red">{`${isRemovePPError}--${cartItem?.name}`}</div>
      )}
      {!hasNoPlans && !cartItem.selected_protection_plan && (
        <ProtectionPlans
          cartItemId={cartItem.id}
          eligiblePlans={cartItem.eligible_protection_plans}
          forPage="checkout"
          onAdd={refreshCheckout}
          onRemove={refreshCheckout}
          selectedPlan={cartItem.selected_protection_plan}
          showInactiveProtectionPlan
        />
      )}
    </div>
  );
};

CheckoutProtectionPlanItem.propTypes = {
  calculateTotal: PropTypes.func,
  cartItem: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    quantity: PropTypes.number,
    selected_protection_plan: PropTypes.shape({
      plan_name: PropTypes.string,
      plan_price: PropTypes.string,
    }),
    eligible_protection_plans: PropTypes.arrayOf(
      PropTypes.shape({
        plan_name: PropTypes.string,
        plan_price: PropTypes.string,
      })
    ),
    stock_discount: PropTypes.bool,
  }),
  client: PropTypes.object,
  getCartIssues: PropTypes.func,
};

export default CheckoutProtectionPlanItem;
